import React, { PureComponent, cloneElement } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

const DISTANCE_FROM_ANCHOR = 20;
const SMALL_DISTANCE_FROM_ANCHOR = 16;

const OverlayTrigger = styled.div`
  position: relative;
  display: inline-block;
`;

const overlayPosition = (
  position,
  shift,
  distance
) => css`
  ${position === 'bottom' && css`
    top: -${distance}px;
    left: calc(50% + ${shift}px);
    transform: translateX(-50%) translateY(-100%);
  `}

  ${position === 'top' && css`
    top: calc(100% + ${distance}px);
    left: calc(50% + ${shift}px);
    transform: translateX(-50%);
  `}

  ${position === 'left' && css`
    top: calc(50% - ${shift}px);
    left: calc(100% + ${distance}px);
    transform: translateY(-50%);
  `}

  ${position === 'right' && css`
    top: calc(50% - ${shift}px);
    left: -${distance}px;
    transform: translateY(-50%) translateX(-100%);
  `}
`;

const Overlay = styled.div`
  ${({
    theme,
    anchorPosition,
    anchorPositionMobile,
    distanceFromAnchor,
    smallDistanceFromAnchor,
    width,
    shift,
    shiftMobile
  }) => css`
    position: absolute;
    z-index: 5;
    width: ${width}px;
    ${overlayPosition(anchorPosition, shift, distanceFromAnchor)}

    @media ${theme.breakpoints.mobile} {
      ${overlayPosition(anchorPositionMobile, shiftMobile, smallDistanceFromAnchor)}
    }
  `}
`;

const ClickableArea = styled.div`
  outline: 0;
  cursor: pointer;
`;

class SideAnchor extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      display: props.display
    };
  }

  componentDidMount() {
    document.addEventListener(this.touchOrClickEvent(), this.onDocumentClick, false);
  }

  componentWillUnmount() {
    document.removeEventListener(this.touchOrClickEvent(), this.onDocumentClick, false);
  }

  onDocumentClick = (e) => {
    if (!this.rootEl || !this.state.display) {
      return;
    }

    const clickInsideComponent = this.rootEl.contains(e.target);

    if (!clickInsideComponent) {
      this.toggle();
    } else if (this.props.onClick) {
      this.props.onClick(e);
    }
  }

  touchOrClickEvent = () => (('ontouchstart' in document) ? 'touchstart' : 'click');

  toggle = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (this.state.display && this.props.onHide) {
      // this.props.onHide(e); // TODO: Check what it does
    } else if (this.props.onShow) {
      this.props.onShow(e);
    }
    this.setState(prevState => ({ display: !prevState.display }));
  }

  makeOverlay = (overlayContent) => {
    const {
      anchorPosition,
      anchorPositionMobile,
      distanceFromAnchor,
      smallDistanceFromAnchor,
      width,
      shift,
      shiftMobile
    } = this.props;
    return (
      <Overlay
        anchorPosition={anchorPosition}
        anchorPositionMobile={anchorPositionMobile}
        distanceFromAnchor={distanceFromAnchor}
        smallDistanceFromAnchor={smallDistanceFromAnchor}
        width={width}
        shift={shift}
        shiftMobile={shiftMobile}
      >
        {typeof overlayContent === 'string'
          ? (
            <div
              dangerouslySetInnerHTML={{ __html: overlayContent }}
            />
          )
          : <div>{overlayContent}</div>}
      </Overlay>
    );
  }

  render() {
    const {
      children,
      overlayContent
    } = this.props;
    const trigger = React.Children.only(children);
    const triggerProps = { ...trigger.props, onClick: this.toggle };

    return (
      <OverlayTrigger
        ref={(el) => { this.rootEl = el; }}
        data-qa={this.props.qa}
      >
        {
          this.state.display && this.makeOverlay(overlayContent)
        }
        <ClickableArea onClick={this.toggle}>
          {cloneElement(trigger, triggerProps)}
        </ClickableArea>
      </OverlayTrigger>
    );
  }

}

SideAnchor.displayName = 'SideAnchor';

SideAnchor.propTypes = {
  anchorPosition: PropTypes
    .oneOf([
      'top',
      'right',
      'bottom',
      'left'
    ]),
  anchorPositionMobile: PropTypes
    .oneOf([
      'top',
      'right',
      'bottom',
      'left'
    ]),
  children: PropTypes.node.isRequired,
  compact: PropTypes.bool,
  /** Used only in `Animated` variant */
  display: PropTypes.bool,
  distanceFromAnchor: PropTypes.number,
  smallDistanceFromAnchor: PropTypes.number,
  floating: PropTypes.bool,
  noSpacing: PropTypes.bool,
  onClick: PropTypes.func,
  onHide: PropTypes.func,
  onShow: PropTypes.func,
  overlayContent: PropTypes.node.isRequired,
  rounded: PropTypes.bool,
  shift: PropTypes.number,
  shiftMobile: PropTypes.number,
  small: PropTypes.bool,
  textAlign: PropTypes
    .oneOf([
      'left',
      'right',
      'center'
    ]),
  type: PropTypes
    .oneOf([
      'positive',
      'negative',
      'neutral',
      'warning',
      'info',
      'tip'
    ]),
  width: PropTypes.number,
  qa: PropTypes.string
};

SideAnchor.defaultProps = {
  anchorPosition: undefined,
  anchorPositionMobile: undefined,
  compact: false,
  display: false,
  distanceFromAnchor: DISTANCE_FROM_ANCHOR,
  smallDistanceFromAnchor: SMALL_DISTANCE_FROM_ANCHOR,
  floating: false,
  noSpacing: false,
  onClick: undefined,
  onHide: undefined,
  onShow: undefined,
  rounded: true,
  shift: 0,
  shiftMobile: 0,
  small: false,
  textAlign: 'left',
  type: 'info',
  width: 0,
  qa: undefined
};

export default SideAnchor;
